import { useEffect, useMemo, useRef, useState } from "react";
import { useDiscardGuard } from "@/lib/discard-guard";
import { useLayer } from "@/lib/layer-stack";

import { I } from "@/components/Icon";

import { CPP_COL_CATS, CPP_COL_LABEL } from "./columns";
import {
  useCppColumnPresets,
  useCreateCppColumnPreset,
  useDeleteCppColumnPreset,
  useSetDefaultCppColumnPreset,
  useClearDefaultCppColumnPreset,
  useUpdateCppColumnPreset,
} from "./hooks";

const StarIcon = ({ filled }: { filled: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth="1.6"
  >
    <path d="M12 2l2.9 6.3 6.6.6-5 4.4 1.5 6.5L12 16.9 5.9 20.3 7.4 13.8l-5-4.4 6.6-.6z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M3 6h18M8 6V4h8v2m-1 0v14H9V6" />
  </svg>
);

const DragIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="9" cy="6" r="1" />
    <circle cx="9" cy="12" r="1" />
    <circle cx="9" cy="18" r="1" />
    <circle cx="15" cy="6" r="1" />
    <circle cx="15" cy="12" r="1" />
    <circle cx="15" cy="18" r="1" />
  </svg>
);

// --- Edit-columns modal (3-pane: category rail | checks | reorder) ---------

function EditColumnsModal({
  appId,
  initial,
  onApply,
  onClose: onCloseProp,
}: {
  appId: number | null;
  initial: string[];
  onApply: (cols: string[]) => void;
  onClose: () => void;
}) {
  const [active, setActive] = useState(CPP_COL_CATS[0].id);
  const [order, setOrder] = useState<string[]>(initial);
  const [query, setQuery] = useState("");
  const [savePreset, setSavePreset] = useState(false);
  const [presetName, setPresetName] = useState("");
  const dragFrom = useRef<number | null>(null);
  const createMut = useCreateCppColumnPreset();

  const guard = useDiscardGuard(true, JSON.stringify(order) !== JSON.stringify(initial) || presetName.trim() !== "", onCloseProp);
  const onClose = guard.requestClose;

  const selected = useMemo(() => new Set(order), [order]);
  const activeCat = CPP_COL_CATS.find((c) => c.id === active) ?? CPP_COL_CATS[0];

  const toggle = (key: string) =>
    setOrder((o) => (o.includes(key) ? o.filter((k) => k !== key) : [...o, key]));

  const items = query
    ? activeCat.items.filter(
        (i) =>
          i.label.toLowerCase().includes(query.toLowerCase()) ||
          i.key.toLowerCase().includes(query.toLowerCase()),
      )
    : activeCat.items;

  const reorder = (to: number) => {
    const from = dragFrom.current;
    dragFrom.current = null;
    if (from == null || from === to) return;
    setOrder((o) => {
      const next = [...o];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const apply = async () => {
    if (savePreset && presetName.trim() && appId != null) {
      try {
        await createMut.mutateAsync({
          app_id: appId,
          name: presetName.trim(),
          columns: order,
        });
      } catch {
        /* name clash etc. — still apply the selection locally */
      }
    }
    onApply(order);
    onCloseProp();
  };

  return (
    <div className="cppt-cm-backdrop" onClick={onClose}>
      <div className="cppt-cm" onClick={(e) => e.stopPropagation()}>
        <div className="cppt-cm-head">
          <div className="cppt-cm-title">Edit columns</div>
          <button className="btn icon sm" onClick={onClose}>
            {I.x}
          </button>
        </div>

        <div className="cppt-cm-search">
          <div className="input-group">
            {I.search}
            <input
              className="input"
              placeholder="Search metrics"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="cppt-cm-body">
          <div className="cppt-cm-rail">
            {CPP_COL_CATS.map((c) => (
              <button
                key={c.id}
                className={"cppt-cm-cat" + (active === c.id ? " on" : "")}
                onClick={() => {
                  setActive(c.id);
                  setQuery("");
                }}
              >
                <span>{c.label}</span>
                <span className="cppt-cm-cat-chev">{I.chevron}</span>
              </button>
            ))}
          </div>

          <div className="cppt-cm-checks">
            <div className="cppt-cm-cat-lbl">{activeCat.label}</div>
            {items.map((i) => (
              <label key={i.key} className="check cppt-cm-check">
                <input
                  type="checkbox"
                  checked={selected.has(i.key)}
                  onChange={() => toggle(i.key)}
                />
                <span>{i.label}</span>
              </label>
            ))}
            {items.length === 0 && (
              <span className="muted" style={{ fontSize: 12 }}>
                No metrics match “{query}”.
              </span>
            )}
          </div>

          <div className="cppt-cm-reorder">
            <div className="cppt-cm-reorder-head">
              <span>Reorder columns</span>
              <span className="cppt-cm-count">{order.length}</span>
            </div>
            {order.length === 0 && (
              <span className="muted" style={{ fontSize: 12 }}>
                Pick metrics on the left.
              </span>
            )}
            {order.map((key, idx) => (
              <div
                key={key}
                className="cppt-cm-roi"
                draggable
                onDragStart={() => (dragFrom.current = idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => reorder(idx)}
              >
                <span className="cppt-cm-roi-grip">
                  <DragIcon />
                </span>
                <span className="cppt-cm-roi-lbl">{CPP_COL_LABEL[key] ?? key}</span>
                <button
                  className="cppt-cm-roi-x"
                  title="Remove"
                  onClick={() => toggle(key)}
                >
                  {I.x}
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="cppt-cm-foot">
          <label className="check">
            <input
              type="checkbox"
              checked={savePreset}
              onChange={() => setSavePreset((v) => !v)}
              disabled={appId == null}
            />
            <span>Save as preset</span>
          </label>
          {savePreset && (
            <input
              className="input cppt-cm-pname"
              placeholder="Preset name"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              autoFocus
            />
          )}
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn primary"
              onClick={apply}
              disabled={createMut.isPending}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Columns button + presets dropdown -------------------------------------

export function CppColumnsControl({
  appId,
  value,
  onChange,
}: {
  appId: number | null;
  value: string[];
  onChange: (cols: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const presets = useCppColumnPresets(appId);
  const setDefault = useSetDefaultCppColumnPreset();
  const clearDefault = useClearDefaultCppColumnPreset();
  const delMut = useDeleteCppColumnPreset();
  const updMut = useUpdateCppColumnPreset();

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useLayer(open, () => setOpen(false));

  const rows = presets.data ?? [];

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      <button className="btn sm" onClick={() => setOpen((o) => !o)}>
        {I.sliders} Columns <span className="cpp-colcount">{value.length}</span>
      </button>
      {open && (
        <div className="cpp-col-pop">
          <div className="cpp-col-head">Saved presets</div>
          {rows.length === 0 && (
            <div className="cppt-preset-empty">No saved presets for this app yet.</div>
          )}
          {rows.map((p) => (
            <div key={p.id} className="cppt-preset-row">
              <button
                className="cppt-preset-apply"
                title="Apply this preset"
                onClick={() => {
                  onChange(p.columns);
                  setOpen(false);
                }}
              >
                <b>{p.name}</b>
                <span className="muted">{p.columns.length} cols</span>
                {p.is_default && <span className="cppt-preset-def">DEFAULT</span>}
              </button>
              <button
                className={"cppt-preset-ico" + (p.is_default ? " on" : "")}
                title={p.is_default ? "Unset default" : "Set as default for this app"}
                onClick={() =>
                  appId != null &&
                  (p.is_default
                    ? clearDefault.mutate({ id: p.id, app_id: appId })
                    : setDefault.mutate({ id: p.id, app_id: appId }))
                }
              >
                <StarIcon filled={p.is_default} />
              </button>
              <button
                className="cppt-preset-ico"
                title="Overwrite with current columns"
                onClick={() =>
                  appId != null &&
                  updMut.mutate({ id: p.id, app_id: appId, columns: value })
                }
              >
                {I.check}
              </button>
              <button
                className="cppt-preset-ico danger"
                title="Delete preset"
                onClick={() => {
                  if (appId != null && window.confirm(`Delete preset “${p.name}”?`))
                    delMut.mutate({ id: p.id, app_id: appId });
                }}
              >
                <TrashIcon />
              </button>
            </div>
          ))}
          <button
            className="cppt-preset-edit"
            onClick={() => {
              setOpen(false);
              setModal(true);
            }}
          >
            {I.sliders} Edit columns
          </button>
        </div>
      )}
      {modal && (
        <EditColumnsModal
          appId={appId}
          initial={value}
          onApply={onChange}
          onClose={() => setModal(false)}
        />
      )}
    </span>
  );
}
